import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme } from '../../types';
import DagrunIcon from '../DagrunIcon';
import {
  FormDef, FormField, FieldType,
  getForm, getFormFields, getRecentSubmissionCount, createSubmission,
} from '../../services/formsService';
import { optionList } from '../../views/project/tabs/FormsTab';

interface Props { pca: IPublicClientApplication; theme: AppTheme }

const PAGE: React.CSSProperties = {
  minHeight: '100vh', background: 'var(--app-bg)', padding: '32px 16px',
  fontFamily: 'var(--font-ui)', display: 'flex', justifyContent: 'center',
};
const CARD: React.CSSProperties = {
  width: '100%', maxWidth: 640, background: '#fff', borderRadius: 12,
  border: '0.5px solid var(--border-light)', boxShadow: 'var(--shadow-card)', padding: 28,
};

export default function PublicFormRenderer({ pca }: Props) {
  const { formId = '' } = useParams();
  const [form, setForm] = useState<FormDef | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rateBlocked, setRateBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = await getForm(pca, formId);
        if (cancelled) return;
        if (f.dagrun_pm_isactive === false) { setLoadError('This form is no longer accepting responses.'); setLoading(false); return; }
        const ff = await getFormFields(pca, formId);
        if (cancelled) return;
        setForm(f); setFields(ff);
        // Rate limit (public forms only).
        if (f.dagrun_pm_ispublic && f.dagrun_pm_anonymousratelimitperhour) {
          const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
          const count = await getRecentSubmissionCount(pca, formId, since).catch(() => 0);
          if (!cancelled && count >= f.dagrun_pm_anonymousratelimitperhour) setRateBlocked(true);
        }
      } catch {
        if (!cancelled) setLoadError('This form could not be loaded. The link may be invalid or you may not have access.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pca, formId]);

  const set = (id: string, v: string) => setValues(prev => ({ ...prev, [id]: v }));

  const missingRequired = useMemo(
    () => fields.filter(f => f.dagrun_pm_isrequired && !(values[f.dagrun_pm_formfieldid] ?? '').trim()),
    [fields, values],
  );
  const allowedExts = useMemo(
    () => (form?.dagrun_pm_attachmentallowedtypes ?? '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
    [form],
  );

  const submit = async () => {
    if (!form || submitting) return;
    if (missingRequired.length > 0) { setSubmitError('Please complete all required fields.'); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      // Build a label->value data map and derive submitter info.
      const data: Record<string, unknown> = {};
      let email: string | null = null, name: string | null = null;
      fields.forEach(f => {
        const v = values[f.dagrun_pm_formfieldid] ?? '';
        data[f.dagrun_pm_name] = v;
        if (f.dagrun_pm_fieldtype === FieldType.Email && v) email = v;
        if (f.dagrun_pm_fieldtype === FieldType.Person && v) name = v;
      });
      await createSubmission(pca, {
        formId, projectId: form._dagrun_pm_projectid_value ?? null,
        data, submitterName: name, submitterEmail: email,
      });
      setDone(true);
    } catch {
      setSubmitError('Your submission could not be sent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={PAGE}>
      <div style={CARD}>
        {/* Branding */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border-light)' }}>
          <DagrunIcon size={28} stroke="#0E7490" />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 400, color: 'var(--ink)' }}>Dagrun PM</span>
        </div>

        {loading ? (
          <div style={{ color: 'var(--ink-4)', fontSize: 14 }}>Loading…</div>
        ) : loadError ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 14 }}>{loadError}</div>
        ) : done ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>Thank you — your submission has been received</div>
          </div>
        ) : rateBlocked ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 14 }}>Submission limit reached — try again later.</div>
        ) : form ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--ink)', margin: '0 0 6px' }}>{form.dagrun_pm_name}</h1>
            {form.dagrun_pm_formdescription && (
              <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 0, marginBottom: 20 }}>{form.dagrun_pm_formdescription}</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {fields.map(f => (
                <FieldInput key={f.dagrun_pm_formfieldid} field={f} value={values[f.dagrun_pm_formfieldid] ?? ''}
                  onChange={v => set(f.dagrun_pm_formfieldid, v)}
                  allowedExts={allowedExts} maxMb={form.dagrun_pm_attachmentmaxmb ?? null} />
              ))}
            </div>
            {submitError && <div style={{ marginTop: 14, color: '#DC2626', fontSize: 13 }}>{submitError}</div>}
            <button type="button" onClick={submit} disabled={submitting}
              style={{ marginTop: 22, background: '#0E7490', color: '#fff', border: 'none', borderRadius: 7, padding: '10px 20px', fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange, allowedExts, maxMb }: {
  field: FormField; value: string; onChange: (v: string) => void;
  allowedExts: string[]; maxMb: number | null;
}) {
  const [fileError, setFileError] = useState<string | null>(null);
  const label = (
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 6 }}>
      {field.dagrun_pm_name}{field.dagrun_pm_isrequired && <span style={{ color: '#DC2626' }}> *</span>}
    </label>
  );
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const file = e.target.files?.[0];
    if (!file) { onChange(''); return; }
    const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
    if (allowedExts.length > 0 && !allowedExts.includes(ext)) {
      setFileError('File type not supported'); onChange(''); e.target.value = ''; return;
    }
    if (maxMb != null && maxMb > 0 && file.size > maxMb * 1024 * 1024) {
      setFileError(`File exceeds ${maxMb} MB`); onChange(''); e.target.value = ''; return;
    }
    onChange(file.name);
  };
  const base: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6,
    fontSize: 14, fontFamily: 'var(--font-ui)', color: 'var(--ink)', boxSizing: 'border-box',
  };
  const ph = field.dagrun_pm_placeholdertext ?? '';

  let control: React.ReactNode;
  switch (field.dagrun_pm_fieldtype) {
    case FieldType.LongText:
      control = <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={ph} rows={4} style={base} />; break;
    case FieldType.Number:
      control = <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={ph} style={base} />; break;
    case FieldType.Date:
      control = <input type="date" value={value} onChange={e => onChange(e.target.value)} style={base} />; break;
    case FieldType.Email:
      control = <input type="email" value={value} onChange={e => onChange(e.target.value)} placeholder={ph || 'name@example.com'} style={base} />; break;
    case FieldType.ChoiceSingle:
      control = (
        <select value={value} onChange={e => onChange(e.target.value)} style={base}>
          <option value="">Select…</option>
          {optionList(parseOptions(field.dagrun_pm_choiceoptionsjson)).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ); break;
    case FieldType.FileAttachment:
      control = (
        <>
          <input type="file" onChange={onFile} style={{ ...base, padding: 6 }} />
          {fileError && <div style={{ marginTop: 4, fontSize: 12, color: '#DC2626' }}>{fileError}</div>}
        </>
      ); break;
    case FieldType.Person:
    case FieldType.Text:
    default:
      control = <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={ph} style={base} />; break;
  }
  return <div>{label}{control}</div>;
}

function parseOptions(json?: string | null): string {
  if (!json) return '';
  try { const a = JSON.parse(json); return Array.isArray(a) ? a.join(', ') : ''; } catch { return ''; }
}
